import type { MapDef, MapId } from '../world/map';
import { T } from '../world/tiles';
import { CHUNK, D, placeholderColor, renderPixels } from './groundPixels';
import { paintStructures } from './structures';

export { D };

type Reply = { id: MapId; key: string; data: Uint8ClampedArray };

/** One shared worker for every map's ground; falls back to the main thread. */
let worker: Worker | null | undefined;
const waiting = new Map<string, (data: Uint8ClampedArray) => void>();
function getWorker(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    worker = new Worker(new URL('./groundWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<Reply>) => {
      const k = `${e.data.id}:${e.data.key}`;
      waiting.get(k)?.(e.data.data);
      waiting.delete(k);
    };
    worker.onerror = () => {
      worker = null;
    };
  } catch {
    worker = null;
  }
  return worker;
}

interface Chunk {
  canvas: HTMLCanvasElement;
  ready: boolean;
}

/** Lazily generated, cached ground chunks for one map (detail pixels built in a worker). */
export class Ground {
  private chunks = new Map<string, Chunk>();
  private inFlight = 0;

  constructor(readonly map: MapDef) {}

  private finish(ch: Chunk, cx: number, cy: number, data: Uint8ClampedArray): void {
    const size = CHUNK * D;
    const c = ch.canvas.getContext('2d')!;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.putImageData(new ImageData(new Uint8ClampedArray(data), size, size), 0, 0);
    c.scale(D, D);
    c.translate(-cx * CHUNK, -cy * CHUNK);
    paintStructures(c, this.map, cx * CHUNK, cy * CHUNK, CHUNK);
    ch.ready = true;
  }

  private placeholder(c: CanvasRenderingContext2D, cx: number, cy: number): void {
    c.save();
    c.scale(D, D);
    c.translate(-cx * CHUNK, -cy * CHUNK);
    const t0x = Math.floor((cx * CHUNK) / T), t0y = Math.floor((cy * CHUNK) / T);
    for (let ty = t0y; ty < t0y + CHUNK / T; ty++) {
      for (let tx = t0x; tx < t0x + CHUNK / T; tx++) {
        const col = placeholderColor(this.map, tx, ty);
        if (!col) continue;
        c.fillStyle = col;
        c.fillRect(tx * T, ty * T, T, T);
      }
    }
    paintStructures(c, this.map, cx * CHUNK, cy * CHUNK, CHUNK);
    c.restore();
  }

  /** Get (or start building) a chunk. `sync` builds it immediately on this thread. */
  chunk(cx: number, cy: number, sync = false): Chunk {
    const key = `${cx},${cy}`;
    let ch = this.chunks.get(key);
    if (ch && (ch.ready || !sync)) return ch;
    if (!ch) {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = CHUNK * D;
      ch = { canvas, ready: false };
      this.chunks.set(key, ch);
      const w = sync ? null : getWorker();
      if (w) {
        this.placeholder(canvas.getContext('2d')!, cx, cy);
        this.inFlight++;
        const target = ch;
        waiting.set(`${this.map.id}:${key}`, (data) => {
          this.inFlight--;
          this.finish(target, cx, cy, data);
        });
        w.postMessage({ id: this.map.id, cx, cy, key });
        return ch;
      }
    }
    this.finish(ch, cx, cy, renderPixels(this.map, cx, cy));
    return ch;
  }

  private range(camX: number, camY: number, viewW: number, viewH: number, margin: number): [number, number, number, number] {
    const mw = this.map.w * T, mh = this.map.h * T;
    return [
      Math.max(0, Math.floor((camX - margin) / CHUNK)),
      Math.max(0, Math.floor((camY - margin) / CHUNK)),
      Math.min(Math.ceil(mw / CHUNK) - 1, Math.floor((camX + viewW + margin) / CHUNK)),
      Math.min(Math.ceil(mh / CHUNK) - 1, Math.floor((camY + viewH + margin) / CHUNK)),
    ];
  }

  /** Draw the visible ground. `ctx` is in device pixels; `z` = device pixels per logical pixel. */
  draw(ctx: CanvasRenderingContext2D, camX: number, camY: number, viewW: number, viewH: number, z: number): void {
    const [x0, y0, x1, y1] = this.range(camX, camY, viewW, viewH, 0);
    ctx.imageSmoothingEnabled = z % D !== 0;
    ctx.imageSmoothingQuality = 'high';
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        const dx = Math.round((cx * CHUNK - camX) * z), dy = Math.round((cy * CHUNK - camY) * z);
        const nx = Math.round(((cx + 1) * CHUNK - camX) * z), ny = Math.round(((cy + 1) * CHUNK - camY) * z);
        ctx.drawImage(this.chunk(cx, cy).canvas, dx, dy, nx - dx, ny - dy);
      }
    }
    ctx.imageSmoothingEnabled = false;
  }

  /** Queue every chunk around a point (called behind the map-change fade). */
  warm(x: number, y: number, radius: number): void {
    const [x0, y0, x1, y1] = this.range(x - radius, y - radius, radius * 2, radius * 2, 0);
    for (let cy = y0; cy <= y1; cy++) for (let cx = x0; cx <= x1; cx++) this.chunk(cx, cy);
  }

  /** Queue a few chunks just outside the view so walking never shows placeholders. */
  prefetch(camX: number, camY: number, viewW: number, viewH: number): void {
    if (this.inFlight > 2) return;
    const [x0, y0, x1, y1] = this.range(camX, camY, viewW, viewH, CHUNK);
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        if (!this.chunks.has(`${cx},${cy}`)) {
          this.chunk(cx, cy);
          return;
        }
      }
    }
  }
}
